import React, { useState } from 'react';
import { X, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';

const BulkPriceUpdateModal = ({ selectedItems, selectionMode, filters, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'increase_percent',
    value: '',
    scope: 'sales',
    apply_to: 'all',
    rounding: 'none',
  });

  const selectedCount = selectionMode === 'ids' ? selectedItems.length : 'filtered';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.value || parseFloat(formData.value) <= 0) {
      setError('Please enter a valid value');
      return false;
    }
    return true;
  };

  const handleProceed = () => {
    if (!validateForm()) return;
    
    // Show confirmation for large operations
    if ((selectionMode === 'ids' && selectedItems.length > 25) || selectionMode === 'filters') {
      setShowConfirm(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        mode: selectionMode,
        item_ids: selectionMode === 'ids' ? selectedItems : [],
        filters: selectionMode === 'filters' ? filters : {},
        operation: formData,
      };

      const response = await itemsAPI.bulkUpdatePrice(payload);
      
      const { totalMatched, totalUpdated, failedIds } = response.data;
      
      if (failedIds.length > 0) {
        toast.error(`Updated ${totalUpdated} items, ${failedIds.length} failed`);
      } else {
        toast.success(`Successfully updated ${totalUpdated} item(s)`);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Bulk price update error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update prices';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getOperationLabel = () => {
    const labels = {
      increase_percent: 'Increase by %',
      decrease_percent: 'Decrease by %',
      increase_amount: 'Increase by ₹',
      decrease_amount: 'Decrease by ₹',
      set_exact: 'Set to ₹',
    };
    return labels[formData.type] || '';
  };

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-center mb-2">Confirm Bulk Update</h3>
            <p className="text-gray-600 text-center mb-4">
              You are about to update prices for <strong>{selectedCount}</strong> item(s).
              This action cannot be undone.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="text-sm"><strong>Operation:</strong> {getOperationLabel()}</p>
              <p className="text-sm"><strong>Value:</strong> {formData.value}</p>
              <p className="text-sm"><strong>Applies to:</strong> {formData.scope === 'both' ? 'Sales & Purchase' : formData.scope === 'sales' ? 'Sales Price' : 'Purchase Price'}</p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Updating...' : 'Confirm Update'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bulk Price Update</h2>
              <p className="text-sm text-gray-600">
                {selectedCount} item(s) selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Operation Type */}
          <div>
            <Label>Operation Type</Label>
            <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="increase_percent">Increase by Percentage</SelectItem>
                <SelectItem value="decrease_percent">Decrease by Percentage</SelectItem>
                <SelectItem value="increase_amount">Increase by Fixed Amount</SelectItem>
                <SelectItem value="decrease_amount">Decrease by Fixed Amount</SelectItem>
                <SelectItem value="set_exact">Set Exact Price</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Value */}
          <div>
            <Label htmlFor="value">
              {formData.type.includes('percent') ? 'Percentage (%)' : 'Amount (₹)'}
            </Label>
            <Input
              id="value"
              type="number"
              value={formData.value}
              onChange={(e) => handleInputChange('value', e.target.value)}
              placeholder={formData.type.includes('percent') ? 'e.g., 10' : 'e.g., 50'}
              min="0"
              step={formData.type.includes('percent') ? '0.1' : '1'}
            />
          </div>

          {/* Scope */}
          <div>
            <Label>Apply to</Label>
            <Select value={formData.scope} onValueChange={(value) => handleInputChange('scope', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sales">Sales Price Only</SelectItem>
                <SelectItem value="purchase">Purchase Price Only</SelectItem>
                <SelectItem value="both">Both Sales & Purchase</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Apply To Filter */}
          <div>
            <Label>Item Type Filter</Label>
            <Select value={formData.apply_to} onValueChange={(value) => handleInputChange('apply_to', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Selected Items</SelectItem>
                <SelectItem value="products">Products Only</SelectItem>
                <SelectItem value="services">Services Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Rounding */}
          <div>
            <Label>Rounding</Label>
            <Select value={formData.rounding} onValueChange={(value) => handleInputChange('rounding', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Rounding</SelectItem>
                <SelectItem value="1">Round to nearest ₹1</SelectItem>
                <SelectItem value="0.5">Round to nearest ₹0.5</SelectItem>
                <SelectItem value="0.1">Round to nearest ₹0.1</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Preview:</strong> This will {getOperationLabel().toLowerCase()} for {formData.scope === 'both' ? 'both sales and purchase prices' : formData.scope === 'sales' ? 'sales prices' : 'purchase prices'}.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleProceed}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Prices'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkPriceUpdateModal;
